import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

export interface Tag {
  id: Identifier;
  name: string;
  tagType: string;
  image?: string;
  original_name?: string;
}

export interface Tagging {
  id: Identifier;
  tagId: Identifier;
  taggableId: Identifier;
  taggableType: string;
  sortOrder?: number;
  tag: Tag;
}

export const fetchTagsByType = async (type: string): Promise<Tag[]> => {
  const response = await api.get('/api/v4/tags', {
    params: {
      type,
      fetch_all: true,
    },
  });

  return camelcaseKeys(response.data) as Tag[];
};

export const workoutPlanTags = async (): Promise<Tag[]> => {
  return fetchTagsByType(
    'workout-plan,difficulty,equipment,badge,workout-time,frequency,objective,alternate-video'
  );
};

export const createTag = async (tag: Partial<Tag>): Promise<Tag> => {
  const response = await api.post('/api/v4/tags', decamelizeKeys(tag));

  return camelcaseKeys(response.data) as Tag;
};

export const fetchLocationTaggings = async (
  locationId: Identifier
): Promise<Tagging[]> => {
  const response = await api.get('/api/v4/taggings', {
    params: {
      taggable_id: locationId,
      taggable_type: 'Fbm::Location',
      tag_type: 'location',
    },
  });

  return camelcaseKeys(response.data.tag) as Tagging[];
};

export const createLocationTagging = async (
  locationId: Identifier,
  tagId: Identifier
): Promise<Tagging> => {
  const response = await api.post('/api/v4/taggings', {
    tagging: {
      taggable_id: locationId,
      taggable_type: 'Fbm::Location',
      tag_id: tagId,
    },
  });

  return camelcaseKeys(response.data) as Tagging;
};

export const deleteLocationTagging = async (
  taggingId: Identifier
): Promise<void> => {
  await api.delete(`/api/v4/taggings/${taggingId}`);
};

export const updateTag = async (tag: Partial<Tag>): Promise<Tag> => {
  const response = await api.post(`/api/v3/tags/update_tag/`, {
    name: tag.name,
    original_name: tag.original_name,
    taggable_type: tag.tagType,
    ...(tag.image ? { image: tag.image } : {}),
  });

  return camelcaseKeys(response.data) as Tag;
};

export const updateWorkoutPlanTag = async (
  id: Identifier,
  tag: Partial<Tag>
): Promise<Tag> => {
  const response = await api.put(`/api/v4/tags/${id}/`, {
    name: tag.name,
    tag_type: tag.tagType,
  });

  return camelcaseKeys(response.data) as Tag;
};

export const deleteTagByName = async (
  tag_name: string,
  type: string
): Promise<void> => {
  await api.post(`/api/v3/tags/delete_tag/`, {
    name: tag_name,
    taggable_type: type,
  });
};

export const deleteTag = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/tags/${id}/`);
};
